import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { roleVerificationService } from '../../services/roleVerificationService';
import AuthUsersPanel from './components/AuthUsersPanel';
import JwtMetadataPanel from './components/JwtMetadataPanel';
import UserProfilePanel from './components/UserProfilePanel';
import SyncStatusPanel from './components/SyncStatusPanel';

const AdminRoleVerificationScreen = () => {
  const { user, userProfile, getClientForRole } = useAuth();
  const [verificationData, setVerificationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadVerificationData();
    }
  }, [user?.id]);

  // Real-time subscription for user_profiles changes
  useEffect(() => {
    if (!user?.id || !autoRefresh) return;

    const adminClient = getClientForRole('admin');
    const channel = adminClient
      ?.channel('role_verification_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user?.id}`
        },
        (payload) => {
          console.log('User profile change detected:', payload);
          loadVerificationData();
        }
      )
      ?.subscribe();

    return () => {
      adminClient?.removeChannel(channel);
    };
  }, [user?.id, autoRefresh, getClientForRole]);

  const loadVerificationData = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const data = await roleVerificationService?.getCurrentUserVerification(user?.id);
      setVerificationData(data);
      setLastUpdated(new Date()?.toISOString());
    } catch (error) {
      console.error('Failed to load verification data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    await loadVerificationData();
  };

  const handleManualSync = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const data = await roleVerificationService?.triggerManualSync(user?.id);
      setVerificationData(data);
      setLastUpdated(new Date()?.toISOString());
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="admin" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Admin Role Verification
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Comprehensive debugging tools for role authentication and data synchronization
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant={autoRefresh ? 'default' : 'outline'}
                size="sm"
                iconName={autoRefresh ? 'Wifi' : 'WifiOff'}
                onClick={toggleAutoRefresh}
              >
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleManualRefresh}
                disabled={isLoading}
              >
                Refresh All
              </Button>
            </div>
          </div>
        </div>

        {/* Current User Info */}
        <div className="bg-card rounded-lg border border-border shadow-md p-4 md:p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
              <Icon name="User" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{userProfile?.full_name || 'Admin User'}</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">User ID</label>
              <p className="text-sm font-mono text-foreground mt-1">{user?.id?.substring(0, 8)}...</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Current Role</label>
              <p className="text-sm font-semibold text-primary mt-1">{userProfile?.role || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Auto Refresh</label>
              <p className="text-sm font-medium text-foreground mt-1">{autoRefresh ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Last Updated</label>
              <p className="text-sm text-foreground mt-1">
                {lastUpdated ? new Date(lastUpdated)?.toLocaleTimeString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Sync Status Panel - Full Width */}
        <div className="mb-6">
          <SyncStatusPanel
            syncStatus={verificationData?.syncStatus}
            loading={isLoading}
            onManualSync={handleManualSync}
          />
        </div>

        {/* Data Source Panels - Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Auth Users Panel */}
          <AuthUsersPanel
            data={verificationData?.authUsers}
            loading={isLoading}
            onRefresh={handleManualRefresh}
            lastUpdated={lastUpdated}
          />

          {/* JWT Metadata Panel */}
          <JwtMetadataPanel
            data={verificationData?.jwtMetadata}
            loading={isLoading}
          />

          {/* User Profile Panel - Full Width */}
          <div className="lg:col-span-2">
            <UserProfilePanel
              data={verificationData?.userProfile}
              loading={isLoading}
            />
          </div>
        </div>

        {/* Debug Information */}
        <div className="mt-6 bg-muted/50 rounded-lg border border-border p-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Debug Information</p>
              <p>This screen displays real-time authentication data from multiple sources to help diagnose role-related issues. Use the sync status panel to identify discrepancies between auth.users, JWT tokens, and user_profiles table.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminRoleVerificationScreen;